import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Users, LayoutGrid, PieChart, Calendar, Bell } from "lucide-react";
import InvestorCard from "@/components/investors/InvestorCard";
import InvestorForm from "@/components/investors/InvestorForm";
import InvestmentForm from "@/components/investors/InvestmentForm";
import ValuationForm from "@/components/investors/ValuationForm";
import DetailPanel from "@/components/shared/DetailPanel";
import ActivityForm from "@/components/shared/ActivityForm";
import TaskForm from "@/components/tasks/TaskForm";
import PortfolioAnalysis from "@/components/investors/PortfolioAnalysis";
import FollowUpTracker from "@/components/investors/FollowUpTracker";
import InvestorCalendar from "@/components/investors/InvestorCalendar";
import FollowUpForm from "@/components/followups/FollowUpForm";

export default function Investors() {
  const [showForm, setShowForm] = useState(false);
  const [editingInvestor, setEditingInvestor] = useState(null);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [showInvestmentForm, setShowInvestmentForm] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showValuationForm, setShowValuationForm] = useState(false);
  const [editingValuation, setEditingValuation] = useState(null);
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [viewMode, setViewMode] = useState("list"); // "list", "portfolio", "calendar", "followup"

  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ["user"],
    queryFn: () => base44.auth.me(),
  });

  const { data: investors = [], isLoading } = useQuery({
    queryKey: ["investors"],
    queryFn: () => base44.entities.Investor.list("-created_date"),
  });

  const { data: allInvestments = [] } = useQuery({
    queryKey: ["investments"],
    queryFn: () => base44.entities.Investment.list("-created_date"),
  });

  const { data: activities = [] } = useQuery({
    queryKey: ["activities", selectedInvestor?.id],
    queryFn: () => base44.entities.Activity.filter({ 
      related_to: "investor", 
      related_id: selectedInvestor?.id 
    }, "-created_date"),
    enabled: !!selectedInvestor,
  });

  const { data: selectedInvestments = [] } = useQuery({
    queryKey: ["investments", selectedInvestor?.id],
    queryFn: () => base44.entities.Investment.filter({ 
      investor_id: selectedInvestor?.id 
    }, "-created_date"),
    enabled: !!selectedInvestor,
  });

  const { data: selectedTasks = [] } = useQuery({
    queryKey: ["tasks", selectedInvestor?.id],
    queryFn: () => base44.entities.Task.filter({ 
      related_to_type: "investor",
      related_to_id: selectedInvestor?.id 
    }, "-created_date"),
    enabled: !!selectedInvestor,
  });

  const { data: allInvestorTasks = [] } = useQuery({
    queryKey: ["investorTasks"],
    queryFn: async () => {
      const tasks = await base44.entities.Task.filter({ 
        related_to_type: "investor"
      }, "-due_date");
      return tasks.filter(task => task.status !== "completed" && task.status !== "cancelled");
    },
  });

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.Investor.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investors"] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Investor.update(id, data),
    onSuccess: (updatedInvestor, variables) => {
      queryClient.invalidateQueries({ queryKey: ["investors"] });
      setShowForm(false);
      setEditingInvestor(null);
      
      // Auto-create follow-up task on status change
      if (variables.data.status && variables.data.status !== selectedInvestor?.status) {
        const taskData = {
          title: `Oppfølging: ${selectedInvestor.name} - Status endret til ${variables.data.status}`,
          description: `Investor status endret fra ${selectedInvestor?.status} til ${variables.data.status}. Følg opp.`,
          status: "todo",
          priority: "high",
          assigned_to: variables.data.responsible_person || selectedInvestor?.responsible_person,
          assigned_to_name: variables.data.responsible_person_name || selectedInvestor?.responsible_person_name,
          related_to_type: "investor",
          related_to_id: selectedInvestor?.id,
          related_to_name: selectedInvestor?.name,
          due_date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split("T")[0], // 3 days from now
        };
        base44.entities.Task.create(taskData);
      }
      
      if (selectedInvestor) {
        queryClient.invalidateQueries({ queryKey: ["tasks"] });
        const refreshed = investors.find(i => i.id === selectedInvestor.id);
        setSelectedInvestor(refreshed);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Investor.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investors"] });
      setSelectedInvestor(null);
    },
  });

  const activityMutation = useMutation({
    mutationFn: (data) => base44.entities.Activity.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      setShowActivityForm(false);
    },
  });

  const createInvestmentMutation = useMutation({
    mutationFn: (data) => base44.entities.Investment.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investments"] });
      setShowInvestmentForm(false);
      setEditingInvestment(null);
    },
  });

  const updateInvestmentMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Investment.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investments"] });
      setShowInvestmentForm(false);
      setEditingInvestment(null);
    },
  });

  const deleteInvestmentMutation = useMutation({
    mutationFn: (id) => base44.entities.Investment.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investments"] });
    },
  });

  const createTaskMutation = useMutation({
    mutationFn: (data) => base44.entities.Task.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      setShowTaskForm(false);
      setEditingTask(null);
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Task.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      setShowTaskForm(false);
      setEditingTask(null);
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (id) => base44.entities.Task.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });

  const handleSubmit = (data) => {
    if (editingInvestor) {
      updateMutation.mutate({ id: editingInvestor.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleInvestmentSubmit = (data) => {
    if (editingInvestment) {
      updateInvestmentMutation.mutate({ id: editingInvestment.id, data });
    } else {
      createInvestmentMutation.mutate(data);
    }
  };

  const getInvestorInvestments = (investorId) => {
    return allInvestments.filter(inv => inv.investor_id === investorId);
  };

  const handleTaskSubmit = (data) => {
    if (editingTask) {
      updateTaskMutation.mutate({ id: editingTask.id, data });
    } else {
      createTaskMutation.mutate(data);
    }
  };

  const handleTaskStatusChange = (task, newStatus) => {
    updateTaskMutation.mutate({ 
      id: task.id, 
      data: { ...task, status: newStatus } 
    });
  };

  const handleUpdateDealStage = (newStage) => {
    if (!selectedInvestor) return;
    updateMutation.mutate({
      id: selectedInvestor.id,
      data: { deal_stage: newStage }
    });
  };

  const handleValuationSubmit = (data) => {
    if (editingValuation) {
      updateInvestmentMutation.mutate({ id: editingValuation.id, data });
    }
  };

  const createFollowUpMutation = useMutation({
    mutationFn: (data) => base44.entities.FollowUp.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["followUps"] });
      setShowFollowUpForm(false);
    },
  });

  const handleFollowUpSubmit = (data) => {
    createFollowUpMutation.mutate(data);
  };

  const filteredInvestors = investors.filter(investor => {
    const matchesSearch = 
      investor.name?.toLowerCase().includes(search.toLowerCase()) ||
      investor.contact_name?.toLowerCase().includes(search.toLowerCase()) ||
      investor.contact_email?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || investor.status === statusFilter;
    const matchesType = typeFilter === "all" || investor.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Investorer</h1>
            <p className="text-slate-500 mt-1">{investors.length} totalt investorer</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 p-1 bg-white rounded-lg border border-slate-200 shadow-sm">
              <button
                onClick={() => setViewMode("list")}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "list"
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <LayoutGrid className="w-4 h-4" />
                <span className="hidden sm:inline">Liste</span>
              </button>
              <button
                onClick={() => setViewMode("portfolio")}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "portfolio"
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <PieChart className="w-4 h-4" />
                <span className="hidden sm:inline">Portefølje</span>
              </button>
              <button
                onClick={() => setViewMode("followup")}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "followup"
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <Bell className="w-4 h-4" />
                <span className="hidden sm:inline">Oppfølging</span>
              </button>
              <button
                onClick={() => setViewMode("calendar")}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "calendar"
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <Calendar className="w-4 h-4" />
                <span className="hidden sm:inline">Kalender</span>
              </button>
            </div>
            <Button onClick={() => setShowForm(true)} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" /> Legg til Investor
            </Button>
          </div>
        </div>

        {/* Content */}
        {viewMode === "portfolio" ? (
          <PortfolioAnalysis investors={investors} investments={allInvestments} />
        ) : viewMode === "followup" ? (
          <FollowUpTracker investors={investors} />
        ) : viewMode === "calendar" ? (
          <InvestorCalendar 
            tasks={allInvestorTasks} 
            onAddMeeting={(date) => {
              setShowTaskForm(true);
              // You can pass the selected date to TaskForm if needed
            }}
          />
        ) : (
          <>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Søk investorer..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 h-11 bg-white border-0 shadow-sm"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40 h-11 bg-white border-0 shadow-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Statuser</SelectItem>
                  <SelectItem value="prospect">Prospekt</SelectItem>
                  <SelectItem value="in_discussion">I Diskusjon</SelectItem>
                  <SelectItem value="committed">Forpliktet</SelectItem>
                  <SelectItem value="invested">Investert</SelectItem>
                  <SelectItem value="exited">Avsluttet</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-40 h-11 bg-white border-0 shadow-sm">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Typer</SelectItem>
                  <SelectItem value="angel">Engelinvestor</SelectItem>
                  <SelectItem value="vc_firm">Venturekapital</SelectItem>
                  <SelectItem value="private_equity">Private Equity</SelectItem>
                  <SelectItem value="family_office">Family Office</SelectItem>
                  <SelectItem value="institutional">Institusjonell</SelectItem>
                  <SelectItem value="individual">Individuell</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Grid */}
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {[1,2,3,4,5,6].map(i => (
                  <div key={i} className="h-48 bg-white rounded-xl animate-pulse" />
                ))}
              </div>
            ) : filteredInvestors.length === 0 ? (
              <div className="text-center py-16">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Ingen investorer funnet</h3>
                <p className="text-slate-500 mb-6">Kom i gang ved å legge til din første investor</p>
                <Button onClick={() => setShowForm(true)} className="bg-slate-900 hover:bg-slate-800">
                  <Plus className="w-4 h-4 mr-2" /> Legg til Investor
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredInvestors.map(investor => (
                  <InvestorCard
                    key={investor.id}
                    investor={investor}
                    investments={getInvestorInvestments(investor.id)}
                    onClick={() => setSelectedInvestor(investor)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Forms & Panels */}
      {showForm && (
        <InvestorForm
          investor={editingInvestor}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowForm(false);
            setEditingInvestor(null);
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {selectedInvestor && (
        <DetailPanel
          item={selectedInvestor}
          type="investor"
          activities={activities}
          investments={selectedInvestments}
          tasks={selectedTasks}
          onClose={() => setSelectedInvestor(null)}
          onEdit={() => {
            setEditingInvestor(selectedInvestor);
            setShowForm(true);
          }}
          onDelete={() => deleteMutation.mutate(selectedInvestor.id)}
          onAddActivity={() => setShowActivityForm(true)}
          onAddInvestment={() => setShowInvestmentForm(true)}
          onAddTask={() => setShowTaskForm(true)}
          onAddFollowUp={() => setShowFollowUpForm(true)}
          onEditTask={(task) => {
            setEditingTask(task);
            setShowTaskForm(true);
          }}
          onDeleteTask={(id) => deleteTaskMutation.mutate(id)}
          onTaskStatusChange={handleTaskStatusChange}
          onUpdateDealStage={handleUpdateDealStage}
          onUpdateValuation={(investment) => {
            setEditingValuation(investment);
            setShowValuationForm(true);
          }}
          isDeleting={deleteMutation.isPending}
        />
      )}

      {showActivityForm && selectedInvestor && (
        <ActivityForm
          relatedTo="investor"
          relatedId={selectedInvestor.id}
          relatedName={selectedInvestor.name}
          onSubmit={(data) => activityMutation.mutate(data)}
          onClose={() => setShowActivityForm(false)}
          isLoading={activityMutation.isPending}
        />
      )}

      {showInvestmentForm && selectedInvestor && (
        <InvestmentForm
          investment={editingInvestment}
          investor={selectedInvestor}
          onSubmit={handleInvestmentSubmit}
          onClose={() => {
            setShowInvestmentForm(false);
            setEditingInvestment(null);
          }}
          isLoading={createInvestmentMutation.isPending || updateInvestmentMutation.isPending}
        />
      )}

      {showTaskForm && selectedInvestor && (
        <TaskForm
          task={editingTask}
          relatedTo="investor"
          relatedId={selectedInvestor.id}
          relatedName={selectedInvestor.name}
          onSubmit={handleTaskSubmit}
          onClose={() => {
            setShowTaskForm(false);
            setEditingTask(null);
          }}
          isLoading={createTaskMutation.isPending || updateTaskMutation.isPending}
        />
      )}

      {showValuationForm && editingValuation && (
        <ValuationForm
          investment={editingValuation}
          onSubmit={handleValuationSubmit}
          onClose={() => {
            setShowValuationForm(false);
            setEditingValuation(null);
          }}
          isLoading={updateInvestmentMutation.isPending}
        />
      )}

      {showFollowUpForm && selectedInvestor && (
        <FollowUpForm
          investor={selectedInvestor}
          onSubmit={handleFollowUpSubmit}
          onClose={() => setShowFollowUpForm(false)}
          isLoading={createFollowUpMutation.isPending}
          currentUser={user}
        />
      )}
    </div>
  );
}